import { useCallback, useState } from "react";

/**
 * Hook to allow a component to offer controlled vs uncontrolled props
 * https://react.dev/learn/sharing-state-between-components#controlled-and-uncontrolled-components
 */
export const useControlledState = <T>(
  controlledValue: T | undefined,
  setControlledValue: ((value: T) => void) | undefined,
  defaultValue: T
): [T, (value: T) => void] => {
  const [internalValue, setInternalValue] = useState<T>(
    controlledValue ?? defaultValue
  );

  // keep state synced with controlled values
  if (controlledValue !== undefined && internalValue !== controlledValue) {
    setInternalValue(controlledValue);
  }

  const setValue = useCallback(
    (value: T) => {
      setInternalValue(value);
      setControlledValue?.(value);
    },
    [setControlledValue]
  );

  return [controlledValue ?? internalValue, setValue];
};
